import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { truncateString } from '@deltek/specification-client/core/Utilities/stringUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import clsx from 'clsx'
import React, { CSSProperties, useCallback, useMemo } from 'react'
import { ListingHeaderVariants } from '../ListingDetailsPage'

const useStyles = makeStyles((theme: Theme) => ({
    button: {
        padding: theme.spacing(0.75, 1.5),
        width: 'auto',
        minWidth: ({ minWidth }: ListingHeaderButtonStyleProps) =>
            !!minWidth ? pxToRem(minWidth) : pxToRem(150),
        height: pxToRem(41),
        borderRadius: 100,
        ...theme.typography.h6,
    },
    buttonClose: {
        backgroundColor: '#5E5F61',
        color: '#e2e2e2',
        '&:hover': {
            backgroundColor: '#5E5F61',
        },
    },
    buttonOpen: {
        border: `1px solid ${theme.palette.common.white}`,
        backgroundColor: '#343334',
    },
    buttonText: {
        fontSize: pxToRem(18),
        lineHeight: pxToRem(16),
    },
    stateButton: {
        lineHeight: '1.95rem',
    },
    notEditableButton: {
        '&:hover': {
            cursor: 'default',
        },
    },
    buttonTextOpen: {
        fontSize: pxToRem(18),
        lineHeight: pxToRem(16),
        color: theme.palette.common.white,
    },
}))

export interface ListingHeaderButtonStyleProps {
    minWidth?: number
}
export interface ListingHeaderButtonProps
    extends ListingHeaderButtonStyleProps {
    buttonLabel: string
    buttonKey: ListingHeaderVariants
    isPopoverOpen: boolean
    handleButtonClick: (event: React.MouseEvent<HTMLButtonElement>) => void
    isOnEditMode?: boolean
    customOpenClass?: CSSProperties
    customClosedClass?: CSSProperties
    disabled?: boolean
}

const ListingHeaderButton: React.FC<ListingHeaderButtonProps> = ({
    buttonLabel,
    buttonKey,
    isPopoverOpen,
    handleButtonClick,
    minWidth,
    isOnEditMode = false,
    customOpenClass,
    customClosedClass,
    disabled = false,
}) => {
    const classes = useStyles({ minWidth })

    const onButtonClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (isOnEditMode) {
                handleButtonClick(event)
            }
        },
        [isOnEditMode, handleButtonClick]
    )

    const truncatedDisplayName = useMemo(() => {
        return truncateString(buttonLabel, 20)
    }, [buttonLabel])

    const isForState = useMemo(() => buttonKey === 'state', [buttonKey])

    return (
        <HoverTooltip text={buttonLabel}>
            <Button
                disableElevation
                disableRipple
                id={buttonKey}
                disabled={disabled}
                className={
                    isPopoverOpen
                        ? clsx(
                              classes.button,
                              classes.buttonOpen,
                              classes.buttonTextOpen
                          )
                        : clsx(
                              classes.button,
                              classes.buttonClose,
                              classes.buttonText,
                              !isOnEditMode && classes.notEditableButton,
                              isForState && classes.stateButton
                          )
                }
                style={{
                    ...customOpenClass,
                }}
                aria-describedby={buttonLabel}
                variant="contained"
                onClick={onButtonClick}
                data-testid={`${buttonKey}EditButton`}
            >
                {truncatedDisplayName}
            </Button>
        </HoverTooltip>
    )
}

export default ListingHeaderButton
